/**
 * The clinical grid every mutating op rounds to: 0.01 cm (0.1 mm), i.e. 2
 * decimal places. Margins and ring offsets are below the achievable voxel
 * resolution past this, so a finer request (e.g. 0.249 cm) is rounded to the
 * grid — deterministically, and disclosed to the clinician. Display, geometry,
 * and the new-structure name then all share one canonical value instead of
 * diverging.
 */
import { formatCmTrimmed } from '~/utils/units';

export const CLINICAL_GRID_DECIMALS = 2;

/** Round a cm value to the {@link CLINICAL_GRID_DECIMALS} clinical grid. */
export function quantizeCm(valueCm: number): number {
    // `* factor / factor` (not `/ grid * grid`) avoids floating-point noise
    // like 0.25000000000000003, so the grid value is exact. Round the
    // magnitude and reapply the sign so an exact half-grid rounds away from
    // zero on both sides; `+ 0` collapses a -0 result to 0.
    const factor = 10 ** CLINICAL_GRID_DECIMALS;
    const magnitude = Math.round(Math.abs(valueCm) * factor) / factor;
    return Math.sign(valueCm) * magnitude + 0;
}

/**
 * Quantize a cm offset to the grid and report the clinician's original
 * request ONLY when it was finer than the grid (so a card can disclose the
 * rounding). The single shared primitive behind every mutating op's
 * grid-rounding — symmetric margin, ring offsets, and the six asymmetric
 * directions all round identically.
 */
export function quantizeCmTracked(valueCm: number): {
    readonly cm: number;
    readonly requestedCm?: number;
} {
    const cm = quantizeCm(valueCm);
    return cm === valueCm ? { cm } : { cm, requestedCm: valueCm };
}

/** One "<number><mm|cm>" distance token; count and replace share this source
 *  so the pair cannot drift. */
const DISTANCE_TOKEN_PATTERN = String.raw`\d+(?:\.\d+)?\s*(mm|cm)`;

/**
 * Replace the single "<number><mm|cm>" token in a model-suggested structure
 * name with the canonical (grid-rounded) value, so the name can never
 * disagree with the applied geometry (e.g. a name of "PTVH+2.49mm" for an
 * applied 2.5 mm margin becomes "PTVH+2.5mm"). The model still authors the
 * base name; only the numeric token is normalized.
 *
 * Rewriting happens ONLY when the name carries exactly one distance token. A
 * name with zero or several tokens (e.g. a ring's "PTV_inner3mm_outer5mm",
 * which carries one canonical offset for two distances) is returned unchanged
 * so a multi-distance name is never corrupted.
 */
export function normalizeDistanceInName(
    name: string,
    canonicalCm: number
): string {
    const tokens = name.match(new RegExp(DISTANCE_TOKEN_PATTERN, 'gi'));
    if (!tokens || tokens.length !== 1) {
        return name;
    }
    return name.replace(
        new RegExp(DISTANCE_TOKEN_PATTERN, 'i'),
        (_match: string, unit: string) => {
            const value =
                unit.toLowerCase() === 'mm' ? canonicalCm * 10 : canonicalCm;
            // The matched token carries no sign; any +/- in the name is part
            // of the base name, so substitute the magnitude only.
            return `${formatCmTrimmed(Math.abs(value))}${unit}`;
        }
    );
}
